import defaultApiClient, { tryCatch } from '@/services/api-client';
import { dataProvider } from '@/services/data-provider';
import {
  CreateLocationBody,
  Location,
  UpdateLocationBody,
  GetLocationsParams,
  CreateAuthTypeRequest,
} from './locations.types';
import { AuthType } from './locations.types';

export interface LocationInfoResponse {
  id: string;
  name: string;
  address: string;
  postal_code: string;
  city: string;
  country_iso2: string;
  language_iso2: string;
  phone: string;
  email: string;
  url: string;
  facebook_url: string;
  instagram_url: string;
  twitter_url: string;
  youtube_url: string;
  linkedin_url: string;
  pinterest_url: string;
  tiktok_url: string;
  snapchat_url: string;
  spotify_url: string;
  applepod_url: string;
  agent_delay: number;
  agent_delay_after_employee: number;
  max_interaction_per_session_sales_widget: number;
  max_interaction_per_session_sales_whatsapp: number;
  max_interaction_per_session_retention_whatsapp: number;
  agent_default_active: boolean;
  max_interaction_per_month: number;
  max_interaction_per_session: number;
  pay_per_use: boolean;
}

export const fetchLocationInfo = async (
  authType: AuthType,
  locationId?: string,
): Promise<LocationInfoResponse> => {
  // Create the authorization header based on auth type
  let authHeader = '';

  if (authType.type === 'basic') {
    // Create base64 encoded basic auth
    const credentials = btoa(`${authType.username}:${authType.password}`);
    authHeader = `Basic ${credentials}`;
  } else if (authType.type === 'api_key') {
    // For API key, we'll use it as a Bearer token or custom header
    if (authType.header_name) {
      // Custom header implementation would go here
      authHeader = `Bearer ${authType.api_key}`;
    } else {
      authHeader = `Bearer ${authType.api_key}`;
    }
  }

  // Build URL with optional location_id for updating
  let url = `/api/v1/locations-external-info?token=${authHeader}`;
  if (locationId) {
    url += `&location_id=${locationId}`;
  }

  try {
    const { data: response, error } = await tryCatch(
      defaultApiClient.get<{ data: LocationInfoResponse }>(url),
    );

    if (error) {
      throw error;
    }

    if (!response?.data) {
      throw new Error('No response data');
    }

    return response?.data.data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(
      'Network error occurred while fetching location information.',
    );
  }
};

export const getLocationsList = async ({
  filter,
  sort,
  order,
  perPage,
  page,
}: GetLocationsParams) => {
  const { data: response, error } = await tryCatch(
    dataProvider.getList<Location>('locations', {
      pagination: { page: page || 1, perPage: perPage || 10 },
      sort: { field: sort || 'updated_at', order: order || 'DESC' },
      filter,
    }),
  );

  if (error) {
    throw error;
  }

  return response;
};

export const getLocation = async (id: string) => {
  const { data: response, error } = await tryCatch(
    dataProvider.getOne<Location>('locations', { id }),
  );

  if (error) {
    throw error;
  }

  return response;
};

export const createLocation = async (data: CreateLocationBody) => {
  const { data: response, error } = await tryCatch(
    dataProvider.create<Location>('locations', {
      data,
    }),
  );

  if (error) {
    throw error;
  }

  return response;
};

export const updateLocation = async (id: string, data: UpdateLocationBody) => {
  const { data: response, error } = await tryCatch(
    dataProvider.update<Location>('locations', {
      id,
      data,
      previousData: {},
    }),
  );

  if (error) {
    throw error;
  }

  return response;
};

export const deleteLocation = async (id: string) => {
  const { data: response, error } = await tryCatch(
    dataProvider.delete<Location>('locations', { id }),
  );

  if (error) {
    throw error;
  }

  return response;
};
export const getLocationAuthTypes = async (id: string) => {
  const { data: response, error } = await tryCatch(
    dataProvider.getList<AuthType>('auth-types', {
      filter: {
        location_id: id,
      },
    }),
  );

  if (error) {
    throw error;
  }

  return response;
};
export const createLocationAuthType = async (data: CreateAuthTypeRequest) => {
  const { data: response, error } = await tryCatch(
    dataProvider.create<AuthType>('auth-types', {
      data,
    }),
  );

  if (error) {
    throw error;
  }

  return response;
};
